import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle, Loader2, Send, X, AlertTriangle } from "lucide-react";
import ApprovalRejectDialog from "@/components/ApprovalRejectDialog";
import ApprovalResubmitDialog from "@/components/ApprovalResubmitDialog";
import SignatureSetupDialog from "@/components/SignatureSetupDialog";
import { FlagChecklist, ApprovalMark, ApprovalLedger, SignatureNudge, ExecSummaryLink, ProposalReadLink } from "@/components/ApprovalBits";
import { PenLine } from "lucide-react";

export default function ApprovalBanner({ bidId, lineItems = [], onChange, signoff }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [hasSig, setHasSig] = useState(null);
  const [approvers, setApprovers] = useState(null);
  const [approverId, setApproverId] = useState("");
  const bdm = !!signoff?.bdm_mode;

  const load = useCallback(() => {
    api.get(`/bids/${bidId}/approvals`).then((r) => setList(r.data)).catch(() => setList([]));
  }, [bidId]);
  useEffect(() => { load(); }, [load, signoff?.approval_id, signoff?.state]);
  useEffect(() => { api.get("/me/signature").then((r) => setHasSig(!!r.data.signature)).catch(() => setHasSig(false)); }, []);
  useEffect(() => {
    api.get(`/bids/${bidId}/approvers`).then((r) => { setApprovers(r.data.approvers || []); setApproverId((cur) => cur || r.data.default || ""); }).catch(() => setApprovers([]));
  }, [bidId, signoff?.state]);

  const refresh = () => { load(); onChange && onChange(); };
  const chosen = (approvers || []).find((a) => a.user_id === approverId);
  const submit = async (note) => {
    if (approvers && approvers.length && !approverId) { toast.error("Pick who approves this bid"); return; }
    setBusy(true);
    try {
      await api.post(`/bids/${bidId}/approvals`, { trigger: "send", note: note || undefined, approver_id: approverId || undefined });
      toast.success(chosen ? `Sent to ${chosen.name} for sign-off` : (bdm ? "Sent to the BDM for sign-off" : "Submitted for approval")); refresh();
    }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.message || d || "Could not submit"); }
    finally { setBusy(false); }
  };
  const picker = approvers && approvers.length > 0 && (
    <label className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="approver-picker" title="Who you may send this to: your manager and everyone above you in the reporting structure (Team → Reports to), any Approver added to this bid's team, the BDM on the pursuit when sign-off applies, and leadership when nobody is set above you.">
      <span className="hidden sm:inline">Send to</span>
      <select value={approverId} onChange={(e) => setApproverId(e.target.value)} data-testid="approver-select"
        className="h-7 text-xs border border-slate-200 rounded-md px-1.5 bg-white text-slate-800 focus:border-[#003E87] outline-none max-w-[220px]">
        {approvers.map((a) => <option key={a.user_id} value={a.user_id}>{a.name} · {a.label}</option>)}
      </select>
    </label>
  );
  const approve = async (id) => {
    setBusy(true);
    try {
      const r = await api.post(`/approvals/${id}/respond`, { action: "approve" }); toast.success(r.data?.approval?.key ? `Approved · ${r.data.approval.key}` : "Approved"); refresh();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
    finally { setBusy(false); }
  };
  const doReject = async (payload) => {
    setBusy(true);
    try { await api.post(`/approvals/${rejectFor}/respond`, { action: "reject", ...payload }); toast.success("Sent back for edits"); setRejectFor(null); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send back"); }
    finally { setBusy(false); }
  };

  if (list === null || !signoff) return null;
  if (signoff.state === "sent") return null;
  // Round-aware: the server picks the current round's request (older rounds never count).
  const active = signoff.approval_id ? list.find((a) => a.approval_id === signoff.approval_id) || null : null;
  const shell = "px-5 py-2.5 border-b text-sm";
  const yellowBtn = "h-7 text-xs border-[#F4C400] text-[#8a6d00] hover:bg-[#F4C400]/10 font-semibold";
  const sigDialog = <SignatureSetupDialog open={sigOpen} onClose={() => setSigOpen(false)} onSaved={() => setHasSig(true)} />;
  const ledger = <ApprovalLedger bidId={bidId} refreshKey={`${active?.status}-${active?.open_flags}-${list.length}`} />;

  if (signoff.state === "stale") {
    return (
      <>
      <div className={`${shell} border-amber-200 bg-amber-50 flex items-center justify-between gap-3`} data-testid="approval-banner-stale">
        <span className="flex items-center gap-2 text-amber-800"><AlertTriangle className="w-4 h-4" /> {signoff.stale_reason ? `${signoff.stale_reason.charAt(0).toUpperCase()}${signoff.stale_reason.slice(1)}.` : `The price changed after sign-off (${money(signoff.approved_total)} approved, now ${money(signoff.current_total)}).`} A new {bdm ? "BDM " : ""}sign-off is needed.</span>
        <div className="flex items-center gap-2">
        {picker}
        <Button size="sm" variant="outline" data-testid="approval-submit-btn" disabled={busy} onClick={() => submit()} className={yellowBtn}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Request new sign-off</>}
        </Button>
        </div>
      </div>
      {signoff.approval_key && <div className="px-5 py-1 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-100" data-testid="approval-voided-note">Approval {signoff.approval_key} is void — a new sign-off is needed before Send.</div>}
      {ledger}
      </>
    );
  }

  if (!active) {
    return (
      <>
      <div className="px-5 py-1.5 flex items-center justify-end gap-2 border-b border-slate-100 bg-white flex-wrap" data-testid="approval-banner-none">
        {approvers && approvers.length === 0 && bdm && signoff.route_error ? (
          <span className="text-[11px] text-rose-600" data-testid="approval-route-error">{signoff.route_error}</span>
        ) : approvers && approvers.length === 0 ? (
          <span className="text-[11px] text-rose-600" data-testid="approval-route-error">Nobody is set above you yet. Ask an admin to set your “Reports to” manager in Team.</span>
        ) : (
          <span className="text-[11px] text-slate-400 hidden sm:inline" data-testid="approval-route-to">
            {bdm ? "Sign-off required before sending" : "Not submitted for approval"}
          </span>
        )}
        {picker}
        <Button size="sm" variant="outline" data-testid="approval-submit-btn" disabled={busy || (approvers && approvers.length === 0)} onClick={() => submit()} className={yellowBtn}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> {chosen ? `Send to ${chosen.name.split(" ")[0]} for sign-off` : "Submit for approval"}</>}
        </Button>
      </div>
      {list.length > 0 && ledger}
      </>
    );
  }
  if (active.status === "pending") {
    const who = active.current_approver_names.join(", ") || "—";
    return (
      <>
      <div className={`${shell} border-amber-200 bg-amber-50`} data-testid="approval-banner-pending">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="flex items-center gap-2 text-amber-800">
            <Clock className="w-4 h-4" />
            {bdm ? <>Waiting on <b>{who}</b>&rsquo;s sign-off{active.step_count > 1 ? ` · step ${active.step_no} of ${active.step_count}` : ""}</> : <>Ball in court: <b>{who}</b> · step {active.step_no} of {active.step_count}</>}
          </span>
          {active.you_can_act && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid="approval-reject-btn" disabled={busy} onClick={() => setRejectFor(active.approval_id)} className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50"><XCircle className="w-3.5 h-3.5 mr-1" /> {bdm ? "Request edits" : "Request changes"}</Button>
              <Button size="sm" data-testid="approval-approve-btn" disabled={busy} onClick={() => approve(active.approval_id)} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {active.owner_override ? "Approve as owner" : "Approve"}</Button>
            </div>
          )}
        </div>
        {active.is_resubmit && active.prev_reject && (
          <div className="mt-1.5 text-[11px] text-rose-700" data-testid="approval-banner-resubmit-note">
            Resubmitted after changes — you asked: “{active.prev_reject.comment}”{active.note ? ` · they replied: “${active.note}”` : ""}
          </div>
        )}
        <ProposalReadLink approval={active} />
        <ExecSummaryLink approval={active} />
        <ApprovalRejectDialog open={!!rejectFor} onClose={() => setRejectFor(null)} onReject={doReject} lineItems={lineItems} busy={busy} />
        {sigDialog}
      </div>
      {ledger}
      </>
    );
  }
  if (active.status === "rejected") {
    const rj = active.reject || {};
    const where = rj.context && rj.context.scope && rj.context.scope !== "general" ? ` · ${rj.context.label || rj.context.scope}` : "";
    const resubmitDialog = (
      <ApprovalResubmitDialog open={resubmitOpen} onClose={() => setResubmitOpen(false)} reject={rj}
        onResubmit={(note) => { setResubmitOpen(false); submit(note); }} busy={busy} />
    );
    // Dismissed → collapse the big red banner into a slim bar, but KEEP the Resubmit path.
    const openFlags = active.open_flags || 0;
    if (dismissed) {
      return (
        <>
        <div className="px-5 py-1.5 flex items-center justify-end gap-2 border-b border-rose-100 bg-white" data-testid="approval-banner-rejected-dismissed">
          <span className="text-[11px] text-rose-600 hidden sm:inline">{bdm ? "Edits were requested" : "Changes were requested"} — {openFlags ? `${openFlags} flag${openFlags === 1 ? "" : "s"} still open` : "not yet resubmitted"}</span>
          <Button size="sm" variant="outline" data-testid="approval-resubmit-btn" disabled={busy || openFlags > 0} title={openFlags ? "Clear every flag first" : ""} onClick={() => setResubmitOpen(true)}
            className={yellowBtn}><Send className="w-3 h-3 mr-1" /> Resubmit</Button>
          <button onClick={() => setDismissed(false)} className="text-[11px] text-slate-400 hover:text-slate-700" data-testid="approval-banner-rejected-expand">Show checklist</button>
          {resubmitDialog}
        </div>
        {ledger}
        </>
      );
    }
    return (
      <>
      <div className={`${shell} border-rose-200 bg-rose-50`} data-testid="approval-banner-rejected">
        <div className="flex items-start justify-between gap-3">
          <div className="text-rose-800">
            <span className="flex items-center gap-2 font-medium"><XCircle className="w-4 h-4" /> {bdm ? "Edits requested" : "Changes requested"} by {rj.by_name}{where}</span>
            {rj.comment && <div className="mt-0.5 text-rose-700/90 pl-6">“{rj.comment}”</div>}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" data-testid="approval-resubmit-btn" disabled={busy || openFlags > 0} title={openFlags ? `Clear ${openFlags} open flag${openFlags === 1 ? "" : "s"} first` : ""} onClick={() => setResubmitOpen(true)} className="h-8 bg-[#F4C400] hover:bg-[#e6b000] text-black font-semibold disabled:opacity-50">{openFlags ? `Resubmit (${openFlags} open)` : "Resubmit"}</Button>
            <button data-testid="approval-banner-rejected-dismiss" title="Dismiss" onClick={() => setDismissed(true)}
              className="p-1 rounded text-rose-400 hover:text-rose-700 hover:bg-rose-100 transition-colors"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <FlagChecklist approval={active} onChange={refresh} />
        {resubmitDialog}
      </div>
      {ledger}
      </>
    );
  }
  return (
    <>
    <div className={`${shell} border-emerald-200 bg-emerald-50 text-emerald-800`} data-testid="approval-banner-approved">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          <>{bdm ? "Signed off by the BDM — ready to send." : "Approved — ready to send."}{active.approval?.key ? ` Record ${active.approval.key} — ${active.approval.by_name || "approver"}, ${active.approval.at_local || active.approval.at || ""}, fingerprint ${String(active.approval.fingerprint || "").slice(0, 12)}. The proposal goes out exactly as it is.` : ""}</>
        </div>
      </div>
      <ApprovalMark approval={active} />
    </div>
    {ledger}
    </>
  );
}
